/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Button,
  Input,
  Table,
  Space,
  Tree,
  Skeleton,
  message,
  Card as AntdCard,
  Tooltip,
  Dropdown,
} from "antd";
import {
  UploadOutlined,
  ReloadOutlined,
  FolderOpenOutlined,
  FileOutlined,
  DownloadOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  CloudOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import UploadEvidenceModal from "./UploadEvidenceModal";
import { Text } from "components/Text";
import { formatDateByLanguage } from "utils/formatDateByLanguage";
import {
  getFilesListByReportUuidAndSearch,
  getStorageListByFilesUuidAndSearch,
  createFiles,
  updateFiles,
} from "services/files.service";
import { PrimaryButton } from "components/SuperAdminAndBusiness/PrimaryButton";

const { Title } = Typography;

const EmptyState = () => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center text-center text-gray-400 py-12 w-full">
      <CloudOutlined className="text-5xl mb-2" />
      <p className="text-sm">{t("noFilesInFolder")}</p>
    </div>
  );
};

const getFileIconTable = (fileName: string) => {
  if (!fileName || typeof fileName !== "string") {
    return <FileTextOutlined style={{ fontSize: 16, color: "#9254de" }} />;
  }

  const ext = fileName.split(".").pop()?.toLowerCase();

  switch (ext) {
    case "pdf":
      return <FilePdfOutlined style={{ fontSize: 16, color: "#d32029" }} />;
    case "jpg":
    case "jpeg":
    case "png":
    case "gif":
    case "webp":
    case "svg":
    case "ico":
    case "bmp":
    case "tiff":
    case "tif":
    case "heic":
    case "heif":
      return <FileImageOutlined style={{ fontSize: 16, color: "#1890ff" }} />;
    case "doc":
    case "docx":
    default:
      return <FileTextOutlined style={{ fontSize: 16, color: "#9254de" }} />;
  }
};

const getFileIconGrid = (fileName: string) => {
  if (!fileName || typeof fileName !== "string") {
    return <FileTextOutlined style={{ fontSize: 32, color: "#9254de" }} />;
  }

  const ext = fileName.split(".").pop()?.toLowerCase();
  switch (ext) {
    case "pdf":
      return <FilePdfOutlined style={{ fontSize: 32, color: "#d32029" }} />;
    case "jpg":
    case "jpeg":
    case "png":
      return <FileImageOutlined style={{ fontSize: 32, color: "#1890ff" }} />;
    case "doc":
    case "docx":
    default:
      return <FileTextOutlined style={{ fontSize: 32, color: "#9254de" }} />;
  }
};

interface EvidenceSectionProps {
  reportData: any;
  isPreliminaryAssessment?: boolean;
  reportIssueData?: any;
}

const EvidenceSection: React.FC<EvidenceSectionProps> = ({
  reportData,
  isPreliminaryAssessment = false,
  reportIssueData,
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState<"list" | "grid">("list");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [folders, setFolders] = useState<any[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [isCloudStorageAvailable, setIsCloudStorageAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [oldTitle, setOldTitle] = useState("");
  const [editingValue, setEditingValue] = useState("");
  const [selectedFolderKey, setSelectedFolderKey] = useState<string | null>(
    null
  );
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [renamingFolder, setRenamingFolder] = useState(false);
  const [showNewFolderDropdown, setShowNewFolderDropdown] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  // Função para converter dados da API em estrutura de Tree
  const convertFoldersToTreeData = (foldersData: any[]) => {
    const map: Record<string, any> = {};
    const roots: any[] = [];

    // Cria um nó para cada pasta
    foldersData.forEach((folder) => {
      map[folder.uuid] = {
        title: folder.folderName + " (" + folder.filesQnt + ")",
        key: folder.uuid,
        icon: <FolderOpenOutlined />,
        children: [],
        data: folder,
      };
    });

    // Monta a árvore
    foldersData.forEach((folder) => {
      const node = map[folder.uuid];
      if (
        folder.parentKey &&
        folder.parentKey !== "" &&
        folder.parentKey !== null &&
        map[folder.parentKey]
      ) {
        map[folder.parentKey].children.push(node);
      } else {
        roots.push(node);
      }
    });

    return roots;
  };

  // Função para converter dados de storage em arquivos
  const convertStorageToFiles = (storageData: any[]) => {
    return storageData.map((file) => ({
      key: file.uuid,
      name: file.name,
      size: `${file.size} MB`,
      modified: new Date(file.createdAt),
      icon: <FileOutlined />,
      data: file,
    }));
  };

  // Carregar dados iniciais
  useEffect(() => {
    let isMounted = true;

    if (reportData?.uuid || reportIssueData?.uuid) {
      const loadInitialData = async () => {
        try {
          setLoading(true);
          const response = await getFilesListByReportUuidAndSearch(
            true, // drive
            isPreliminaryAssessment ? "" : reportData?.uuid || "",
            isPreliminaryAssessment ? reportIssueData?.uuid : "",
            searchTerm // search
          );

          if (isMounted) {
            const treeData = convertFoldersToTreeData(response.data);
            setFolders(treeData);

            // Selecionar primeira pasta se não houver seleção
            if (treeData.length > 0 && !selectedFolderKey) {
              const firstKey = treeData[0].key;
              setSelectedFolderKey(firstKey);
              await loadFiles(firstKey);
            }
          }
        } catch (error) {
          if (isMounted) {
            console.error("Erro ao carregar pastas:", error);
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };

      loadInitialData();
    }

    return () => {
      isMounted = false;
    };
  }, [reportData?.uuid, reportIssueData?.uuid, isPreliminaryAssessment]);

  // Recarregar quando search term mudar
  useEffect(() => {
    let isMounted = true;

    if (reportData?.uuid || reportIssueData?.uuid) {
      const loadSearchData = async () => {
        try {
          setLoading(true);
          const response = await getFilesListByReportUuidAndSearch(
            true, // drive
            isPreliminaryAssessment ? "" : reportData?.uuid || "", // reportUuid
            isPreliminaryAssessment ? reportIssueData?.uuid : "", // reportIssuesUuid (vazio para buscar todas)
            searchTerm // search
          );

          if (isMounted) {
            const treeData = convertFoldersToTreeData(response.data);
            setFolders(treeData);
          }
        } catch (error) {
          if (isMounted) {
            console.error("Erro ao carregar pastas:", error);
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };

      loadSearchData();
    }

    return () => {
      isMounted = false;
    };
  }, [
    searchTerm,
    reportData?.uuid,
    reportIssueData?.uuid,
    isPreliminaryAssessment,
  ]);

  // Carregar pastas
  const loadFolders = async () => {
    try {
      setLoading(true);
      const response = await getFilesListByReportUuidAndSearch(
        true, // drive
        isPreliminaryAssessment ? "" : reportData?.uuid || "", // reportUuid
        isPreliminaryAssessment ? reportIssueData?.uuid : "", // reportIssuesUuid (vazio para buscar todas)
        searchTerm // search
      );

      const treeData = convertFoldersToTreeData(response.data);
      setFolders(treeData);

      // Selecionar primeira pasta se não houver seleção
      if (treeData.length > 0 && !selectedFolderKey) {
        const firstKey = treeData[0].key;
        setSelectedFolderKey(firstKey);
        await loadFiles(firstKey);
      }
    } catch (error) {
      console.error("Erro ao carregar pastas:", error);
    } finally {
      setLoading(false);
    }
  };

  // Carregar arquivos de uma pasta
  const loadFiles = async (folderUuid: string) => {
    try {
      setLoading(true);
      const response = await getStorageListByFilesUuidAndSearch(
        folderUuid,
        searchTerm
      );

      const filesData = convertStorageToFiles(response.data);
      setFiles(filesData);
    } catch (error) {
      console.error("Erro ao carregar arquivos:", error);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadFolders();
  };

  const insertFolderRecursive = (
    folders: any[],
    parentKey: string,
    newFolder: any
  ): any[] => {
    return folders.map((folder) => {
      if (folder.key === parentKey) {
        const children = folder.children || [];
        return {
          ...folder,
          children: [...children, newFolder],
        };
      } else if (folder.children) {
        return {
          ...folder,
          children: insertFolderRecursive(
            folder.children,
            parentKey,
            newFolder
          ),
        };
      }
      return folder;
    });
  };

  const handleNewFolder = async () => {
    setShowNewFolderDropdown(true);
    setNewFolderName("");
  };

  const handleCreateFolder = async () => {
    if (!reportData?.uuid) return;

    const trimmedName = newFolderName.trim();
    if (!trimmedName) {
      message.error(t("folderNameRequired"));
      return;
    }

    setCreatingFolder(true);
    try {
      const parentKey = selectedFolderKey ?? null;
      const dto = {
        folderName: trimmedName,
        parentKey,
        drive: true,
        reportUuid: reportData.uuid,
        reportIssuesUuid: reportIssueData?.uuid,
      };
      await createFiles(dto);
      message.success(t("folderCreated"));
      await loadFolders();
      setShowNewFolderDropdown(false);
      setNewFolderName("");
    } catch (err) {
      message.error(t("errorCreatingFolder"));
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleCancelNewFolder = () => {
    setShowNewFolderDropdown(false);
    setNewFolderName("");
  };

  const handleOpenUploadModal = () => setUploadModalOpen(true);
  const handleCloseUploadModal = () => setUploadModalOpen(false);

  const handleSelectFolder = async (
    selectedKeys: React.Key[],
    { selected }: { selected: boolean }
  ) => {
    const selectedKey = selectedKeys?.[0]?.toString() || null;

    // Evita reprocessar se a mesma pasta for clicada novamente
    if (!selected || selectedKey === selectedFolderKey) return;

    setSelectedFolderKey(selectedKey);
    if (selectedKey) {
      await loadFiles(selectedKey);
    }
  };

  const handleUploadSuccess = () => {
    // Recarrega a pasta atual se houver uma selecionada
    if (selectedFolderKey) {
      loadFiles(selectedFolderKey);
      handleRefresh();
    }
  };

  const columns = [
    {
      title: t("name"),
      dataIndex: "name",
      key: "name",
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span
            className="font-medium"
            style={{
              display: "block",
              width: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              verticalAlign: "middle",
            }}
          >
            {getFileIconTable(text)} {text}
          </span>
        </Tooltip>
      ),
    },
    {
      title: t("size"),
      dataIndex: "size",
      key: "size",
      width: 120,
    },
    {
      title: t("modified"),
      dataIndex: "modified",
      key: "modified",
      width: 180,
      render: (date: Date) => {
        return <span>{formatDateByLanguage(date)}</span>;
      },
    },
    {
      title: t("actions"),
      key: "actions",
      width: 100,
      render: (_, record: any) => (
        <Space>
          <Button
            type="text"
            icon={<DownloadOutlined />}
            onClick={() => handleDownloadFile(record)}
          />
          {/* <Button
            type="text"
            icon={<ShareAltOutlined />}
            onClick={() => handleShareFile(record)}
          /> */}
        </Space>
      ),
    },
  ];

  const handleDownloadFile = (file: any) => {
    if (file.data.url) {
      window.open(file.data.url, "_blank");
    } else {
      message.error(t("fileNotFound"));
    }
  };

  // const handleShareFile = (file: any) => {
  //   // Implementar compartilhamento do arquivo
  // };

  const updateFolderTitle = (
    nodes: any[],
    key: string,
    newTitle: string
  ): any[] => {
    return nodes.map((node) => {
      if (node.key === key) {
        return { ...node, title: newTitle };
      } else if (node.children) {
        return {
          ...node,
          children: updateFolderTitle(node.children, key, newTitle),
        };
      }
      return node;
    });
  };

  const handleRenameFolder = async (key: string, newTitle: string) => {
    if (oldTitle === newTitle || newTitle === "") {
      setEditingKey(null);
      setEditingValue("");
      return;
    }
    setRenamingFolder(true);
    try {
      await updateFiles(key, { folderName: newTitle });
      setFolders((prev) => updateFolderTitle(prev, key, newTitle));
      setEditingKey(null);
      setEditingValue("");
      message.success(t("folderRenamed"));
      await loadFolders();
    } catch (err) {
      message.error(t("errorRenamingFolder"));
    } finally {
      setRenamingFolder(false);
    }
  };

  const renderFolderTitle = (node: any) => {
    if (editingKey === node.key) {
      return (
        <Input
          size="small"
          autoFocus
          value={editingValue}
          onChange={(e) => setEditingValue(e.target.value)}
          onBlur={() =>
            handleRenameFolder(node.key, editingValue.trim() || node.title)
          }
          onPressEnter={() =>
            handleRenameFolder(node.key, editingValue.trim() || node.title)
          }
          onClick={(e) => e.stopPropagation()}
          style={{
            width: "100%",
            minWidth: 80,
            maxWidth: 180,
            height: 28,
            padding: "2px 8px",
            background: "transparent",
            borderRadius: 4,
            boxShadow: "none",
            outline: "1px solid #d9d9d9",
            fontSize: 14,
          }}
        />
      );
    }
    return (
      <span
        onDoubleClick={(e) => {
          e.stopPropagation();
          setEditingKey(node.key);
          setOldTitle(node.title);
          setEditingValue(node.title);
        }}
        style={{ userSelect: "none", cursor: "pointer" }}
      >
        <span>{node.title}</span>
      </span>
    );
  };

  const newFolderDropdownContent = (
    <div className="p-3 bg-white border rounded-lg shadow-lg min-w-[300px] z-50 bg-white-A700">
      <div className="mb-3">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {t("folderName")}
        </label>
        <Input
          placeholder={t("enterFolderName")}
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onPressEnter={handleCreateFolder}
          autoFocus
          size="large"
          className="border border-gray-300 rounded-md"
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button
          size="small"
          onClick={handleCancelNewFolder}
          disabled={creatingFolder}
        >
          {t("cancel")}
        </Button>
        <PrimaryButton
          size="small"
          onClick={handleCreateFolder}
          loading={creatingFolder}
          disabled={!newFolderName.trim()}
        >
          {t("save")}
        </PrimaryButton>
      </div>
    </div>
  );

  return isCloudStorageAvailable ? (
    <Card className="shadow-md">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-4">
        <div>
          <div className="flex flex-col lg:flex-row lg:items-start lg:gap-2 mb-5 lg:mb-0">
            <Title level={4} className="!mb-2 !text-lg lg:!text-xl">
              {t("evidenceCollection")}
            </Title>

            <Tooltip title={t("oneDriveConnected")}>
              <div className="flex items-center gap-2 justify-center rounded-lg bg-green-200 px-2 text-green-800 text-base font-medium h-10 lg:h-auto">
                <CloudOutlined className="text-green-800" />
                {t("oneDrive")}
              </div>
            </Tooltip>
          </div>

          <Text className="text-sm lg:text-base">
            {t("evidenceCollectionDescription")}
          </Text>
        </div>
        <div className="flex flex-col lg:flex-row gap-2 w-full lg:w-auto relative">
          <Button
            icon={<ReloadOutlined />}
            className="w-full lg:w-auto text-base text-center min-w-[150px] flex items-center justify-center gap-2"
            size="large"
            style={{
              background: "var(--white_A700)",
              color: "var(--primary_color)",
            }}
            onClick={handleRefresh}
            loading={loading}
          >
            {t("refresh")}
          </Button>
          <Dropdown
            open={showNewFolderDropdown}
            onOpenChange={setShowNewFolderDropdown}
            dropdownRender={() => newFolderDropdownContent}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              icon={<FolderOpenOutlined />}
              className="w-full lg:w-auto text-base text-center min-w-[150px] flex items-center justify-center gap-2"
              size="large"
              style={{
                background: "var(--white_A700)",
                color: "var(--primary_color)",
              }}
              onClick={handleNewFolder}
              loading={creatingFolder}
            >
              {t("newFolder")}
            </Button>
          </Dropdown>
          <Button
            type="default"
            className="w-full lg:w-auto text-base text-center min-w-[150px] flex items-center justify-center gap-2"
            size="large"
            style={{
              background: "var(--primary_color)",
              color: "var(--white_A700)",
            }}
            icon={<UploadOutlined />}
            onClick={handleOpenUploadModal}
          >
            {t("uploadFiles")}
          </Button>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-between gap-2">
        <Input
          placeholder={t("searchFiles")}
          className="w-full rounded-md border-[#e7ebec]"
          size="large"
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="flex gap-1 border rounded-md p-2 w-full lg:w-auto justify-center">
          <Button
            type={view === "list" ? "default" : "text"}
            size="small"
            icon={<UnorderedListOutlined />}
            onClick={() => setView("list")}
            className={
              view === "list" ? "!border !border-blue-300 !bg-blue-50" : ""
            }
          />
          <Button
            type={view === "grid" ? "default" : "text"}
            size="small"
            icon={<AppstoreOutlined />}
            onClick={() => setView("grid")}
            className={
              view === "grid" ? "!border !border-blue-300 !bg-blue-50" : ""
            }
          />
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 mt-4">
        <div className="w-full lg:w-1/4 border p-3 lg:p-4 rounded-md">
          <Title level={5} className="!mb-3 !text-base lg:!text-lg">
            {t("folders")}
          </Title>
          <div className="bg-blue-50 border-blue-200 px-2 py-1 rounded-md">
            <div className="flex items-center gap-2 text-blue-700">
              <FolderOpenOutlined />
              <span className="font-medium text-sm lg:text-base">
                {reportData?.code || reportIssueData?.reportCode} -{" "}
                {t("complaint")}
              </span>
            </div>
          </div>
          <div className="mt-2">
            {loading ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : (
              <Tree
                showIcon
                treeData={folders}
                selectable={true}
                selectedKeys={selectedFolderKey ? [selectedFolderKey] : []}
                expandedKeys={expandedKeys}
                onExpand={(keys) => setExpandedKeys(keys as string[])}
                onSelect={handleSelectFolder}
                titleRender={renderFolderTitle}
              />
            )}
          </div>
        </div>
        <div className="w-full lg:w-3/4 border p-3 lg:p-4 rounded-md min-h-[320px]">
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : files.length === 0 ? (
            <div className="w-full h-full flex items-center justify-center">
              <EmptyState />
            </div>
          ) : view === "grid" ? (
            <div className="w-full flex flex-wrap gap-4 justify-start items-stretch">
              {files.map((file) => (
                <AntdCard
                  key={file.key}
                  className="flex flex-col items-center justify-between rounded-lg border border-gray-100 shadow-sm p-4 bg-white"
                  style={{ width: 180 }}
                  bodyStyle={{ padding: 0 }}
                >
                  <div className="flex flex-col items-center justify-center py-4 w-full">
                    <div className="mb-2">{getFileIconGrid(file.name)}</div>
                    <Tooltip title={file.name} placement="top">
                      <div
                        className="font-medium text-xs text-center w-full truncate"
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 150,
                          margin: "0 auto",
                        }}
                      >
                        {file.name}
                      </div>
                    </Tooltip>
                    <div className="text-gray-400 text-xs">{file.size}</div>
                  </div>
                  <div className="flex justify-center gap-2 w-full px-3 pb-3">
                    <Button
                      icon={<DownloadOutlined />}
                      size="small"
                      className="flex-1"
                      onClick={() => handleDownloadFile(file)}
                    >
                      {t("download")}
                    </Button>
                    {/* <Button
                      icon={<ShareAltOutlined />}
                      
                      size="small"
                      className="flex-1"
                      onClick={() => handleShareFile(file)}
                    /> */}
                  </div>
                </AntdCard>
              ))}
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table
                columns={columns}
                dataSource={files}
                pagination={false}
                className="bg-white rounded-md"
              />
            </div>
          )}
        </div>
      </div>
      <UploadEvidenceModal
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        selectedFolderKey={selectedFolderKey}
        reportUuid={reportData?.uuid}
        reportIssuesUuid={reportIssueData?.uuid}
        onUploadSuccess={handleUploadSuccess}
      />
    </Card>
  ) : (
    <Card className="shadow-md">
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-2">
        <div>
          <Title level={4} className="!mb-0 !text-lg lg:!text-xl">
            {t("evidenceCollection")}
          </Title>
          <Text className="text-sm lg:text-base">
            {t("evidenceCollectionDescription")}
          </Text>
        </div>
        <div className="flex items-center px-3 lg:px-4 py-2 bg-yellow-50 text-yellow-700 rounded-md">
          <ExclamationCircleOutlined className="mr-2 text-yellow-700" />
          <span className="text-sm text-yellow-700">
            {t("noCloudStorageWarning")}
          </span>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center py-8 lg:py-12">
        <CloudOutlined className="text-gray-300 text-6xl lg:text-8xl mb-4 lg:mb-6" />
        <Title
          level={3}
          className="!mb-2 !text-xl lg:!text-2xl text-center lg:text-left"
        >
          {t("noCloudStorageTitle")}
        </Title>
        <Text className="text-sm lg:text-base text-center max-w-md">
          {t("noCloudStorageDescription")}
        </Text>
      </div>
    </Card>
  );
};

export default EvidenceSection;
